import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ADMIN_DASHBOARD,
  LEARNER_DASHBOARD,
  ERROR_ACCESS_DENIED,
} from '@/constants/constant-routes';
import {
  PERMISSION_LIST_KEY,
  DASHBOARD_PERMISSION,
  LEARNER_DASHBOARD_PERMISSION,
} from '@/constants/constant-permissions';
import { getAvailableRoute } from '@/constants/constant-permission-route-mapping';

interface IndexRedirectProps {
  requiredPermission: string;
  defaultRoute: string;
  /** Route prefix to filter available routes (e.g. '/admin' or '/learn') */
  routePrefix?: string;
  fallbackRoute?: string;
}

/**
 * Redirects to the default route if user has the required permission,
 * otherwise finds the first route the user has permission for (filtered by routePrefix).
 */
export function IndexRedirect({
  requiredPermission,
  defaultRoute,
  routePrefix,
  fallbackRoute = '/access-denied',
}: IndexRedirectProps) {
  const userPermissions: string[] = JSON.parse(
    sessionStorage.getItem(PERMISSION_LIST_KEY) || '[]',
  );

  if (userPermissions.includes(requiredPermission)) {
    return <Navigate to={defaultRoute} replace />;
  }

  const firstRoute = getAvailableRoute(userPermissions, routePrefix);
  return <Navigate to={firstRoute || fallbackRoute} replace />;
}

export default function SmartRedirect() {
  const { userMode, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Get user permissions from sessionStorage (parsed as array for exact matching)
  const userPermissions: string[] = JSON.parse(
    sessionStorage.getItem(PERMISSION_LIST_KEY) || '[]',
  );

  // Helpers to find best route per mode
  const findAdminRoute = (): string | null => {
    if (userPermissions.includes(DASHBOARD_PERMISSION.DASHBOARD_READ)) {
      return ADMIN_DASHBOARD;
    }
    return getAvailableRoute(userPermissions, '/admin');
  };

  const findLearnerRoute = (): string | null => {
    if (userPermissions.includes(LEARNER_DASHBOARD_PERMISSION.LEARNER_DASHBOARD_READ)) {
      return LEARNER_DASHBOARD;
    }
    return getAvailableRoute(userPermissions, '/learn');
  };

  // Determine redirect based on user mode and permissions
  if (userMode.currentMode === 'admin' && userMode.hasAdminRole) {
    const adminRoute = findAdminRoute();
    if (adminRoute) return <Navigate to={adminRoute} replace />;
    // Fallback: try learner routes if user also has learner role
    if (userMode.hasLearnerRole) {
      const learnerRoute = findLearnerRoute();
      if (learnerRoute) return <Navigate to={learnerRoute} replace />;
    }
    return <Navigate to={ERROR_ACCESS_DENIED} replace />;
  } else if (userMode.currentMode === 'learner' && userMode.hasLearnerRole) {
    const learnerRoute = findLearnerRoute();
    if (learnerRoute) return <Navigate to={learnerRoute} replace />;
    // Fallback: try admin routes if user also has admin role
    if (userMode.hasAdminRole) {
      const adminRoute = findAdminRoute();
      if (adminRoute) return <Navigate to={adminRoute} replace />;
    }
    return <Navigate to={ERROR_ACCESS_DENIED} replace />;
  } else if (userMode.hasAdminRole) {
    const adminRoute = findAdminRoute();
    if (adminRoute) return <Navigate to={adminRoute} replace />;
    return <Navigate to={ERROR_ACCESS_DENIED} replace />;
  } else if (userMode.hasLearnerRole) {
    const learnerRoute = findLearnerRoute();
    if (learnerRoute) return <Navigate to={learnerRoute} replace />;
    return <Navigate to={ERROR_ACCESS_DENIED} replace />;
  } else if (userPermissions.length > 0) {
    // User has permissions but no matching admin/learner route — send to access denied
    return <Navigate to={ERROR_ACCESS_DENIED} replace />;
  } else {
    // No permissions at all — user is authenticated but has nothing assigned
    return <Navigate to={ERROR_ACCESS_DENIED} replace />;
  }
}